import { FooterV2 } from '@common/components/Footer/FooterV2';
import { Routes } from '@common/constants';
import { useTranslations } from '@lang';
import { useCallback, useMemo } from 'react';
import { useRouter } from 'shared-routing';
import type { MobileTabItem, SideNavItem } from 'voyager';
import { AppShell } from 'voyager';
import { Logo } from './components/Logo';
import type { LayoutProps } from './GeneralLayout';
import { ShellWrapper, TopBar } from './GeneralLayoutV2.styles';
import { VoyagerThemeBridge } from './VoyagerThemeBridge';

export function GeneralLayoutV2({
  children,
  property,
  language,
  basketCount = 0,
  guest,
  termsAndConditionPage = null,
  privacyAndCookiePolicyPage = null,
  cancellationPage = null,
}: LayoutProps) {
  const router = useRouter();
  const { t } = useTranslations();

  const isHome = router.pathname === Routes.HOME;

  const goHome = useCallback(() => router.push({ pathname: Routes.HOME }), [router]);
  const goBasket = useCallback(() => router.push({ pathname: Routes.BASKET }), [router]);

  const accountRoute = guest ? Routes.MY_ACCOUNT : Routes.SIGNIN;
  const goAccount = useCallback(
    () => router.push({ pathname: accountRoute }),
    [router, accountRoute]
  );

  const navItems: SideNavItem[] = useMemo(
    () => [
      { id: 'explore', label: t('nav.explore'), icon: 'explore', onClick: goHome },
      {
        id: 'basket',
        label: t('app.basket'),
        icon: 'basket',
        badge: basketCount,
        onClick: goBasket,
      },
    ],
    [goHome, goBasket, basketCount, t]
  );

  const bottomNavItems: SideNavItem[] = useMemo(
    () => [
      {
        id: 'account',
        label: guest ? t('nav.account') : t('common.signIn'),
        icon: 'account',
        onClick: goAccount,
      },
      { id: 'language', label: t('nav.language'), icon: 'language' },
    ],
    [goAccount, guest, t]
  );

  const mobileNavItems: MobileTabItem[] = useMemo(
    () => [
      { id: 'explore', label: t('nav.explore'), icon: 'explore', onClick: goHome },
      {
        id: 'basket',
        label: t('app.basket'),
        icon: 'basket',
        badge: basketCount,
        onClick: goBasket,
      },
      {
        id: 'account',
        label: guest ? t('nav.account') : t('common.signIn'),
        icon: 'account',
        onClick: goAccount,
      },
      { id: 'language', label: t('nav.language'), icon: 'language' },
    ],
    [goHome, goBasket, basketCount, goAccount, guest, t]
  );

  return (
    <ShellWrapper>
      <VoyagerThemeBridge />
      <AppShell
        navItems={navItems}
        bottomNavItems={bottomNavItems}
        mobileNavItems={mobileNavItems}
        navigationLabel={t('aria.mainNavigation')}
        footer={
          <FooterV2
            property={property}
            language={language}
            termsAndConditionPage={termsAndConditionPage}
            privacyAndCookiePolicyPage={privacyAndCookiePolicyPage}
            cancellationPage={cancellationPage}
          />
        }
      >
        {!isHome && (
          <TopBar>
            <Logo logo={property.logo} name={property.name} />
          </TopBar>
        )}

        {children}
      </AppShell>
    </ShellWrapper>
  );
}
